import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["screen", "tab", "goalsPanel", "rangeLink", "sectionInput"]
  static values = {
    catchupUrl: String,
    ensureRecommendationsUrl: String,
    recommendationsEnabled: Boolean,
    initialScreen: String
  }

  connect() {
    const initialScreen = this.initialScreenValue || new URL(window.location.href).searchParams.get("section") || window.location.hash.replace("#", "") || "analytics"
    this.showScreen(initialScreen)
    this.#scheduleCatchup()
  }

  disconnect() {
    clearTimeout(this.catchupTimer)
  }

  switch(event) {
    this.showScreen(event.params.screen)
    this.#updateLocationSection(event.params.screen)
  }

  toggleGoals() {
    this.goalsPanelTarget.classList.toggle("hidden")
  }

  showScreen(screenName) {
    this.screenTargets.forEach((screen) => {
      screen.classList.toggle("hidden", screen.dataset.youtubeReportScreen !== screenName)
    })

    this.tabTargets.forEach((tab) => {
      const active = tab.dataset.youtubeReportScreen === screenName
      tab.classList.toggle("stub-button-primary", active)
      tab.classList.toggle("stub-button-outline", !active)
    })

    this.#updateRangeLinks(screenName)
    this.#updateSectionInputs(screenName)
    this.#ensureRecommendations(screenName)
  }

  #updateLocationSection(screenName) {
    if (!this.#validScreen(screenName)) return

    const url = new URL(window.location.href)
    url.searchParams.set("section", screenName)
    url.hash = ""
    window.history.replaceState(window.history.state, "", url)
  }

  #updateRangeLinks(screenName) {
    if (!this.#validScreen(screenName)) return

    this.rangeLinkTargets.forEach((link) => {
      const url = new URL(link.href)
      url.searchParams.set("section", screenName)
      url.hash = ""
      link.href = url.toString()
    })
  }

  #updateSectionInputs(screenName) {
    if (!this.#validScreen(screenName)) return

    this.sectionInputTargets.forEach((input) => {
      input.value = screenName
    })

    this.element.querySelectorAll('input[name="section"]').forEach((input) => {
      input.value = screenName
    })
  }

  #validScreen(screenName) {
    return this.screenTargets.some((screen) => screen.dataset.youtubeReportScreen === screenName)
  }

  #scheduleCatchup() {
    if (!this.hasCatchupUrlValue) return

    this.catchupTimer = setTimeout(() => this.#catchupSections(), 750)
  }

  async #catchupSections() {
    try {
      const response = await fetch(this.catchupUrlValue, {
        headers: { Accept: "text/vnd.turbo-stream.html" }
      })
      if (!response.ok) return

      const html = await response.text()
      window.Turbo?.renderStreamMessage(html)
    } catch (_error) {
      // Broadcasts continue to update the report; catch-up is only a missed-message guard.
    }
  }

  #ensureRecommendations(screenName) {
    if (screenName !== "recommendations") return
    if (!this.recommendationsEnabledValue) return
    if (!this.hasEnsureRecommendationsUrlValue) return
    if (this.recommendationsRequested) return

    this.recommendationsRequested = true

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    fetch(this.ensureRecommendationsUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken || "",
        Accept: "text/vnd.turbo-stream.html"
      }
    }).then(async (response) => {
      if (!response.ok) {
        this.recommendationsRequested = false
        return
      }

      const html = await response.text()
      window.Turbo?.renderStreamMessage(html)
    }).catch(() => {
      this.recommendationsRequested = false
    })
  }
}
